"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import type { Product } from "@/config/products";
import { ProductCard } from "@/components/products/product-card";
import { HeaderSection } from "@/components/shared/header-section";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";
import { cn } from "@/lib/utils";
import { Sparkles, Gamepad2, Shield, Zap, Target, Wrench, ArrowRight } from "lucide-react";
import { motion, AnimatePresence, type Variants } from "motion/react";

interface HomeProductsSectionProps {
  products?: Product[];
}

function getCategoryIcon(cat: string) {
  const c = cat.toLowerCase();
  if (c.includes("macro")) return Zap;
  if (c.includes("cheat")) return Shield;
  if (c.includes("aimbot")) return Target;
  if (c.includes("tool") || c.includes("spoofer")) return Wrench;
  return Gamepad2;
}

const headerContainerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.05,
    },
  },
};

const headerItemVariants: Variants = {
  hidden: { opacity: 0, y: 25, filter: "blur(6px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      type: "spring",
      stiffness: 120,
      damping: 20,
    },
  },
};

const gridContainerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.15,
    },
  },
};

const cardItemVariants: Variants = {
  hidden: { opacity: 0, y: 30, scale: 0.96, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: {
      type: "spring",
      stiffness: 140,
      damping: 20,
    },
  },
};

export default function HomeProductsSection({
  products: initialProducts = [],
}: HomeProductsSectionProps) {
  const allProducts = useMemo(() => initialProducts ?? [], [initialProducts]);
  const [activeTab, setActiveTab] = useState<string>("ALL");

  // Prioritize featured products first
  const sortedProducts = useMemo(() => {
    return [...allProducts].sort((a, b) => {
      if (a.isFeatured && !b.isFeatured) return -1;
      if (!a.isFeatured && b.isFeatured) return 1;
      return 0;
    });
  }, [allProducts]);

  // Dynamically build filter tabs from existing products
  const filterTabs = useMemo(() => {
    const tabs: Array<{ id: string; label: string; icon?: React.ComponentType<{ className?: string }> }> = [
      { id: "ALL", label: "All Products", icon: Sparkles },
    ];

    const games = new Set<string>();
    const categories = new Set<string>();

    allProducts.forEach((p) => {
      if (p.gameTag && p.gameTag !== "ALL_GAMES") games.add(p.gameTag);
      if (p.category) categories.add(p.category.toLowerCase());
    });

    games.forEach((game) => {
      tabs.push({ id: `game:${game}`, label: game, icon: Gamepad2 });
    });

    categories.forEach((cat) => {
      tabs.push({
        id: `cat:${cat}`,
        label: cat.charAt(0).toUpperCase() + cat.slice(1),
        icon: getCategoryIcon(cat),
      });
    });

    return tabs;
  }, [allProducts]);

  const filteredProducts = useMemo(() => {
    return sortedProducts.filter((product) => {
      if (activeTab === "ALL") return true;
      if (activeTab.startsWith("game:")) {
        const g = activeTab.replace("game:", "");
        return product.gameTag === g;
      }
      if (activeTab.startsWith("cat:")) {
        const c = activeTab.replace("cat:", "").toLowerCase();
        return product.category?.toLowerCase() === c;
      }
      return true;
    });
  }, [sortedProducts, activeTab]);

  // Limit home page grid to 6 products
  const displayedProducts = useMemo(() => {
    return filteredProducts.slice(0, 6);
  }, [filteredProducts]);

  return (
    <section id="products" className="relative scroll-mt-20 py-16 md:py-24">
      {/* Background radial highlight */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-1/2 -z-10 h-96 -translate-y-1/2 bg-[radial-gradient(ellipse_at_center,rgba(99,102,241,0.08),transparent_70%)]"
      />

      <MaxWidthWrapper className="flex w-full flex-col gap-10">
        {/* Header */}
        <motion.div
          className="flex flex-col items-center text-center"
          variants={headerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
        >
          <motion.div variants={headerItemVariants}>
            <HeaderSection
              label="Featured Products"
              title="Undetected Software & Hardware Macros"
              subtitle="Engineered for competitive players. Instant license key dispatch upon checkout."
            />
          </motion.div>

          {/* Dynamic Filter Pills */}
          <motion.div
            variants={headerItemVariants}
            className="mt-8 flex flex-wrap justify-center gap-2 rounded-2xl border border-border/60 bg-muted/30 p-1.5 backdrop-blur-md dark:bg-muted/20"
          >
            {filterTabs.map((tab) => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;

              return (
                <motion.button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  className={cn(
                    "relative flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-bold transition-all sm:text-sm",
                    isActive
                      ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                      : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                  )}
                >
                  {Icon && <Icon className="size-3.5" />}
                  <span>{tab.label}</span>
                </motion.button>
              );
            })}
          </motion.div>
        </motion.div>

        {/* Products Grid */}
        <motion.div
          className={cn(
            "mx-auto grid w-full max-w-7xl grid-cols-1 justify-items-center gap-6 sm:grid-cols-2 lg:gap-8",
            displayedProducts.length >= 4
              ? "lg:grid-cols-3 2xl:grid-cols-4"
              : "lg:grid-cols-3",
          )}
          variants={gridContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          layout
        >
          <AnimatePresence mode="popLayout">
            {displayedProducts.map((product) => (
              <motion.div
                key={product.id}
                layout
                variants={cardItemVariants}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
                className="flex w-full max-w-[420px] justify-center"
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filteredProducts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm font-medium text-muted-foreground">
              No products found for this category.
            </p>
          </div>
        )}

        {/* Explore All Products CTA Button */}
        {filteredProducts.length > 6 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="flex items-center justify-center pt-4"
          >
            <Link
              href="/pricing"
              className="group relative inline-flex items-center gap-2.5 rounded-full border border-primary/30 bg-primary/10 px-8 py-3.5 text-sm font-extrabold text-primary shadow-lg backdrop-blur-md transition-all duration-300 hover:border-primary hover:bg-primary hover:text-primary-foreground hover:shadow-xl hover:shadow-primary/25"
            >
              <span>Explore All Products</span>
              <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        )}
      </MaxWidthWrapper>
    </section>
  );
}
